import { Eye, EyeOff } from "lucide-react";
import {
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
} from "react";

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
};

export const Field = ({
  label,
  error,
  hint,
  id: providedId,
  type,
  ...inputProps
}: FieldProps) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isPassword = type === "password";
  const resolvedType = isPassword && isPasswordVisible ? "text" : type;

  return (
    <label className="auth-field" htmlFor={id}>
      <span className="auth-field__meta">
        <span className="auth-field__label">{label}</span>
        {hint ? <span className="auth-field__hint">{hint}</span> : null}
      </span>
      <span className={error ? "auth-field__control has-error" : "auth-field__control"}>
        <input
          {...inputProps}
          id={id}
          type={resolvedType}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        {isPassword ? (
          <button
            type="button"
            className="auth-field__reveal"
            onClick={() => setIsPasswordVisible((visible) => !visible)}
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
          >
            {isPasswordVisible ? (
              <EyeOff size={18} strokeWidth={1.8} />
            ) : (
              <Eye size={18} strokeWidth={1.8} />
            )}
          </button>
        ) : null}
      </span>
      {error ? (
        <span className="auth-field__error" id={`${id}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
};

type PrimaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isLoading?: boolean;
  loadingLabel?: string;
};

export const PrimaryButton = ({
  children,
  isLoading = false,
  loadingLabel = "Working…",
  ...buttonProps
}: PrimaryButtonProps) => (
  <button
    {...buttonProps}
    className="auth-submit"
    disabled={isLoading || buttonProps.disabled}
  >
    <span>{isLoading ? loadingLabel : children}</span>
  </button>
);
